import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';

const NewPPPoEClientPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [useFixedIp, setUseFixedIp] = useState(false);
  const [plans, setPlans] = useState([]);
  const [routers, setRouters] = useState([]);
  const [importedSecrets, setImportedSecrets] = useState([]);
  const [pools, setPools] = useState([]);
  const [secretSearch, setSecretSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    plan_id: '',
    router_id: '',
    pppoe_username: '',
    pppoe_password: '',
    local_address: '',
    remote_address: '',
    linked_secret_id: '',
    payment_day: '15',
    notes: ''
  });

  useEffect(() => {
    loadPlans();
    loadRouters();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await api.get('/api/pppoe/plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Error cargando planes:', error);
    }
  };

  const loadRouters = async () => {
    try {
      const response = await api.get('/api/routers');
      setRouters(response.data);
    } catch (error) {
      console.error('Error cargando routers:', error);
    }
  };

  const loadImportedSecrets = async (routerId) => {
    if (!routerId) {
      setImportedSecrets([]);
      return;
    }
    try {
      const response = await api.get(`/api/pppoe/imported-secrets/${routerId}`);
      setImportedSecrets(response.data.secrets);
    } catch (error) {
      console.error('Error cargando secrets importados:', error);
      setImportedSecrets([]);
    }
  };

  const loadPools = async (routerId) => {
    if (!routerId) {
      setPools([]);
      return;
    }
    try {
      const response = await api.get(`/api/pppoe/pools/${routerId}`);
      setPools(response.data.pools);
    } catch (error) {
      console.error('Error cargando pools:', error);
      setPools([]);
    }
  };

  const handleSecretSelect = (secretId) => {
    if (!secretId) {
      // Si deselecciona, limpiar campos
      setFormData({
        ...formData,
        linked_secret_id: '',
        pppoe_username: '',
        pppoe_password: '',
        local_address: '',
        remote_address: ''
      });
      return;
    }

    const secret = importedSecrets.find(s => s.id === secretId);
    if (secret) {
      setFormData({
        ...formData,
        linked_secret_id: secretId,
        pppoe_username: secret.name,
        pppoe_password: secret.password,
        local_address: secret.local_address || '',
        remote_address: secret.remote_address || ''
      });
    }
  };

  const handleRouterChange = (routerId) => {
    setFormData({
      ...formData,
      router_id: routerId,
      linked_secret_id: '',
      pppoe_username: '',
      pppoe_password: '',
      local_address: '',
      remote_address: ''
    });
    loadImportedSecrets(routerId);
    loadPools(routerId);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    // 1. Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      alert('❌ Formato de email inválido');
      return;
    }
    
    // 2. Validar teléfono (7-15 dígitos según país)
    const phoneRegex = /^\d{7,15}$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      alert('❌ El teléfono debe tener entre 7 y 15 dígitos');
      return;
    }
    
    setLoading(true);

    try {
      const data = {
        ...formData,
        payment_day: parseInt(formData.payment_day)
      };

      const response = await api.post('/api/pppoe/clients', data);
      
      let message = '✅ Cliente creado exitosamente';
      if (response.data.payment_day_normalized) {
        message += `\n📅 Día de pago normalizado al día 28 del mes`;
      }
      
      alert(message);
      navigate('/pppoe');
    } catch (error) {
      console.error('Error creando cliente:', error);
      alert(error.response?.data?.detail || '❌ Error al crear cliente');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find(p => p.id === formData.plan_id);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'
    }`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate('/pppoe')}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
          >
            <ArrowLeft className={isDark ? 'text-white' : 'text-gray-900'} />
          </button>
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Nuevo Cliente PPPoE
            </h1>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              Registra un nuevo cliente de internet fijo
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className={`rounded-xl shadow-lg p-6 ${
          isDark ? 'bg-gray-800/50' : 'bg-white'
        }`}>
          {/* Información del Cliente */}
          <div className="mb-6">
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Información del Cliente
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-white border-gray-300 text-gray-900' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Juan Pérez"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Teléfono *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-white border-gray-300 text-gray-900' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="3121234567"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-white border-gray-300 text-gray-900' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="cliente@email.com"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Dirección
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-white border-gray-300 text-gray-900' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Calle 123, Colonia"
                />
              </div>
            </div>
          </div>

          {/* Configuración del Servicio */}
          <div className="mb-6">
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Configuración del Servicio
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Plan PPPoE *
                </label>
                <select
                  required
                  value={formData.plan_id}
                  onChange={(e) => setFormData({...formData, plan_id: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-white border-gray-300 text-gray-900' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Selecciona un plan...</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - {plan.download_speed}/{plan.upload_speed} - ${plan.price_monthly}/mes
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Router *
                </label>
                <select
                  required
                  value={formData.router_id}
                  onChange={(e) => handleRouterChange(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-white border-gray-300 text-gray-900' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Selecciona un router...</option>
                  {routers.map((router) => (
                    <option key={router.id} value={router.id}>
                      {router.name}
                    </option>
                  ))}
                </select>
              </div>

              {formData.router_id && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Ligar con Secret (Opcional)
                  </label>
                  
                  {importedSecrets.length > 5 && (
                    <input
                      type="text"
                      placeholder="🔍 Buscar secret..."
                      value={secretSearch}
                      onChange={(e) => setSecretSearch(e.target.value)}
                      className={`w-full px-4 py-2 rounded-lg border mb-2 ${
                        isDark 
                          ? 'bg-white border-gray-300 text-gray-900 placeholder-gray-500' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  )}
                  
                  <select
                    value={formData.linked_secret_id}
                    onChange={(e) => handleSecretSelect(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDark 
                        ? 'bg-white border-gray-300 text-gray-900' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Crear secret nuevo</option>
                    {importedSecrets
                      .filter(secret => 
                        secretSearch === '' || 
                        secret.name.toLowerCase().includes(secretSearch.toLowerCase())
                      )
                      .map(secret => (
                        <option key={secret.id} value={secret.id}>
                          {secret.name} ({secret.local_address || 'Sin IP'} → {secret.remote_address || 'Sin Pool'})
                        </option>
                      ))
                    }
                  </select>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {importedSecrets.length === 0 
                      ? 'No hay secrets importados. Se creará uno nuevo.' 
                      : `${importedSecrets.filter(s => secretSearch === '' || s.name.toLowerCase().includes(secretSearch.toLowerCase())).length} secrets disponibles`}
                  </p>
                </div>
              )}

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Usuario PPPoE *
                </label>
                <input
                  type="text"
                  required
                  value={formData.pppoe_username}
                  onChange={(e) => setFormData({...formData, pppoe_username: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-white border-gray-300 text-gray-900' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="cliente001"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Contraseña PPPoE *
                </label>
                <input
                  type="text"
                  required
                  value={formData.pppoe_password}
                  onChange={(e) => setFormData({...formData, pppoe_password: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-white border-gray-300 text-gray-900' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="password123"
                />
              </div>

              {/* Checkbox para habilitar IP fija */}
              <div className="flex items-center gap-3 mb-4">
                <input
                  type="checkbox"
                  id="useFixedIp"
                  checked={useFixedIp}
                  onChange={(e) => setUseFixedIp(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="useFixedIp" className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Asignar IP fija manualmente (opcional)
                </label>
              </div>

              {useFixedIp && (
                <>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      IP del Router (Local Address)
                    </label>
                <input
                  type="text"
                  
                  value={formData.local_address}
                  onChange={(e) => setFormData({...formData, local_address: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-white border-gray-300 text-gray-900' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="IP fija específica (ej: 192.168.100.1)"
                />
                <datalist id="local-pools">
                  {pools.map(pool => (
                    <option key={pool.name} value={pool.name}>
                      {pool.ranges}
                    </option>
                  ))}
                </datalist>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  IP del gateway/router (si quieres una IP fija para este cliente)
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  IP del Cliente (Remote Address)
                </label>
                <input
                  type="text"
                  
                  value={formData.remote_address}
                  onChange={(e) => setFormData({...formData, remote_address: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-white border-gray-300 text-gray-900' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="IP fija específica (ej: 192.168.100.10)"
                />
                <datalist id="remote-pools">
                  {pools.map(pool => (
                    <option key={pool.name} value={pool.name}>
                      {pool.ranges}
                    </option>
                  ))}
                </datalist>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  IP del cliente (si quieres asignarle una IP fija específica)
                </p>
              </div>
                </>
              )}

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Día de Pago Mensual *
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  max="31"
                  value={formData.payment_day}
                  onChange={(e) => setFormData({...formData, payment_day: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-white border-gray-300 text-gray-900' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="15"
                />
                {parseInt(formData.payment_day) >= 29 && (
                  <p className={`text-xs mt-1 flex items-center gap-1 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                    <AlertCircle className="w-3 h-3" />
                    Se normalizará al día 28 para evitar problemas con meses cortos
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Plan Summary */}
          {selectedPlan && (
            <div className={`mb-6 p-4 rounded-lg border-2 ${
              isDark
                ? 'bg-blue-900/20 border-blue-700'
                : 'bg-blue-50 border-blue-300'
            }`}>
              <h3 className={`font-semibold mb-2 ${isDark ? 'text-blue-300' : 'text-blue-900'}`}>
                Resumen del Plan Seleccionado
              </h3>
              <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <div>
                  <span className="font-semibold">Plan:</span> {selectedPlan.name}
                </div>
                <div>
                  <span className="font-semibold">Velocidad:</span> {selectedPlan.download_speed}/{selectedPlan.upload_speed}
                </div>
                <div>
                  <span className="font-semibold">Precio:</span> ${selectedPlan.price_monthly}/mes
                </div>
                <div>
                  <span className="font-semibold">Días de gracia:</span> {selectedPlan.grace_days} días
                </div>
              </div>
            </div>
          )}

          {/* Notas */}
          <div className="mb-6">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Notas / Comentarios
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows="3"
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark 
                  ? 'bg-white border-gray-300 text-gray-900' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Información adicional del cliente..."
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/pppoe')}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                isDark
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
              }`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              {loading ? 'Creando...' : 'Crear Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewPPPoEClientPage;
